// Admin member management. Lists the full roster and lets an admin change each
// member's role (member/admin), account status (active/suspended), and
// membership category (manual override — no Stripe charge).
//
// Guard rails mirror the API: an admin can't demote or suspend their own
// account (the server returns 403 self_lockout; we also hide those controls for
// the row that is the current admin).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  fetchMembers,
  updateMemberAdmin,
  setMemberMembershipAdmin,
  clearMemberMembershipAdmin,
  setHouseholdNightAllowanceAdmin,
  fetchMembershipTypes,
  fetchInboundSummary,
} from '../api';
import MemberConversation from './MemberConversation.jsx';

const STATUS_FILTERS = ['all', 'active', 'suspended'];
const ROLE_FILTERS = ['all', 'member', 'admin'];

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

export default function AdminMembers({ myMemberId }) {
  const { instance } = useMsal();
  const account = instance.getActiveAccount();
  const accountKey = account?.homeAccountId || account?.username || null;

  const [members, setMembers] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [noticeById, setNoticeById] = useState({});
  const [convMember, setConvMember] = useState(null); // member being messaged
  const [inbound, setInbound] = useState({}); // memberId -> {inbound_count, last_inbound_at}

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchMembers(instance, account, {
        status: statusFilter === 'all' ? undefined : statusFilter,
        role: roleFilter === 'all' ? undefined : roleFilter,
        search: query || undefined,
      });
      if (res.ok) {
        setMembers((res.data && res.data.members) || []);
      } else {
        setError((res.data && (res.data.message || res.data.error)) || 'Could not load members.');
      }
    } catch {
      setError('Network error loading members.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountKey, statusFilter, roleFilter, query]);

  useEffect(() => { load(); }, [load]);

  // Membership tiers for the category dropdown (loaded once).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchMembershipTypes(instance, account);
        if (!cancelled && res.ok) {
          setTiers((res.data && (res.data.membershipTypes || res.data.types || res.data)) || []);
        }
      } catch { /* non-fatal — the dropdown just won't populate */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountKey]);

  // Inbound-reply summary for badges (who has texted/emailed back). Non-fatal.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchInboundSummary(instance, account);
        if (!cancelled && res.ok) {
          const map = {};
          for (const row of (res.data?.summary || [])) map[row.member_id] = row;
          setInbound(map);
        }
      } catch { /* badges are best-effort */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountKey]);

  const tierList = useMemo(() => (Array.isArray(tiers) ? tiers : []), [tiers]);

  function flash(id, msg) {
    setNoticeById((m) => ({ ...m, [id]: msg }));
    setTimeout(() => setNoticeById((m) => {
      const next = { ...m };
      delete next[id];
      return next;
    }), 3000);
  }

  function replaceRow(updated) {
    if (!updated) return;
    setMembers((rows) => rows.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
  }

  async function patchMember(row, patch, successMsg) {
    setBusyId(row.id);
    setError(null);
    try {
      const res = await updateMemberAdmin(instance, account, row.id, patch);
      if (res.ok) {
        replaceRow(res.data && res.data.member);
        flash(row.id, successMsg);
      } else {
        setError((res.data && (res.data.message || res.data.error)) || 'Update failed.');
      }
    } catch {
      setError('Network error updating member.');
    } finally {
      setBusyId(null);
    }
  }

  async function changeCategory(row, value) {
    // value '' means "remove category" (comp access, no tier); otherwise assign.
    if (value === row.membership_type_id) return; // no-op
    setBusyId(row.id);
    setError(null);
    try {
      const res = value
        ? await setMemberMembershipAdmin(instance, account, row.id, value)
        : await clearMemberMembershipAdmin(instance, account, row.id);
      if (res.ok) {
        replaceRow(res.data && res.data.member);
        flash(row.id, value ? 'Membership category updated.' : 'Membership category removed.');
      } else {
        setError((res.data && (res.data.message || res.data.error)) ||
          (value ? 'Could not change category.' : 'Could not remove category.'));
      }
    } catch {
      setError('Network error changing category.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="card">
      <h2>Members</h2>
      <p className="muted">Manage roles, account status, and membership category. Changes take effect immediately.</p>

      <form
        className="member-search"
        onSubmit={(e) => { e.preventDefault(); setQuery(search.trim()); }}
      >
        <input
          type="search"
          placeholder="Search name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search members"
        />
        <button type="submit" className="chip">Search</button>
        {query && (
          <button
            type="button"
            className="chip"
            onClick={() => { setSearch(''); setQuery(''); }}
          >
            Clear
          </button>
        )}
      </form>

      <div className="app-filter">
        <span className="filter-label">Status:</span>
        {STATUS_FILTERS.map((f) => (
          <button
            key={f}
            className={f === statusFilter ? 'chip chip-active' : 'chip'}
            onClick={() => setStatusFilter(f)}
          >
            {f}
          </button>
        ))}
        <span className="filter-label">Role:</span>
        {ROLE_FILTERS.map((f) => (
          <button
            key={f}
            className={f === roleFilter ? 'chip chip-active' : 'chip'}
            onClick={() => setRoleFilter(f)}
          >
            {f}
          </button>
        ))}
        <button className="chip" onClick={load} title="Refresh">↻</button>
      </div>

      {error && <p className="app-error" role="alert">{error}</p>}
      {loading && <p className="muted">Loading…</p>}

      {!loading && members.length === 0 && <p className="muted">No members match.</p>}

      {!loading && members.length > 0 && (
        <ul className="member-list">
          {members.map((m) => {
            const isSelf = myMemberId
              && String(myMemberId).toLowerCase() === String(m.id).toLowerCase();
            const busy = busyId === m.id;
            return (
              <li className="member-row" key={m.id}>
                <div className="member-main">
                  <div className="member-id">
                    <strong>{m.display_name || m.email}</strong>
                    {isSelf && <span className="member-you">you</span>}
                    <span className={`member-badge member-badge-${m.status}`}>{m.status}</span>
                    <span className={`member-badge member-role-${m.role}`}>{m.role}</span>
                    {inbound[m.id]?.inbound_count > 0 && (
                      <span className="member-badge member-reply" title="This member has replied">
                        ↩ {inbound[m.id].inbound_count} repl{inbound[m.id].inbound_count === 1 ? 'y' : 'ies'}
                      </span>
                    )}
                  </div>
                  <p className="muted member-meta">
                    {m.email}
                    {m.phone_e164 ? ` · ${m.phone_e164}` : ''}
                    {' · joined '}{fmtDate(m.created_at)}
                  </p>
                  <p className="muted member-meta">
                    Membership: {m.tier_name
                      ? `${m.tier_name} (${m.membership_status})`
                      : 'none'}
                  </p>
                  {m.household_id && (
                    <HouseholdNights
                      member={m}
                      disabled={busy}
                      onSaved={(household) => {
                        if (household) {
                          setMembers((rows) => rows.map((r) => (
                            r.household_id === m.household_id
                              ? { ...r, annual_cabin_nights: household.annual_cabin_nights }
                              : r
                          )));
                        }
                        flash(m.id, 'Household night allowance updated.');
                      }}
                      onError={setError}
                      setBusyId={setBusyId}
                      instance={instance}
                      account={account}
                    />
                  )}
                  {noticeById[m.id] && <p className="member-notice">{noticeById[m.id]}</p>}
                </div>

                <div className="member-actions">
                  <label className="member-control">
                    <span>Role</span>
                    <select
                      value={m.role}
                      disabled={busy || isSelf}
                      title={isSelf ? "You can't change your own role" : 'Change role'}
                      onChange={(e) => patchMember(m, { role: e.target.value }, 'Role updated.')}
                    >
                      <option value="member">member</option>
                      <option value="admin">admin</option>
                    </select>
                  </label>

                  <label className="member-control">
                    <span>Status</span>
                    <select
                      value={m.status}
                      disabled={busy || isSelf}
                      title={isSelf ? "You can't change your own status" : 'Change status'}
                      onChange={(e) => patchMember(m, { status: e.target.value },
                        e.target.value === 'suspended' ? 'Member suspended.' : 'Member reactivated.')}
                    >
                      <option value="active">active</option>
                      <option value="suspended">suspended</option>
                    </select>
                  </label>

                  <label className="member-control">
                    <span>Category</span>
                    <select
                      value={m.membership_type_id || ''}
                      disabled={busy || tierList.length === 0}
                      title="Set or remove membership category (manual override, no charge). Select None for comp access with no paid tier."
                      onChange={(e) => changeCategory(m, e.target.value)}
                    >
                      <option value="">
                        {tierList.length === 0 ? 'no tiers' : 'None (no category)'}
                      </option>
                      {tierList.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </label>

                  <button
                    type="button"
                    className="btn-secondary member-message-btn"
                    onClick={() => setConvMember(m)}
                    title="Email or text this member and see their replies"
                  >
                    ✉ Message
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {convMember && (
        <MemberConversation member={convMember} onClose={() => setConvMember(null)} />
      )}
    </div>
  );
}

// Per-household annual cabin-night allowance editor, shown on rows whose member
// belongs to a household. Displays used-vs-allowed for the current membership
// year and lets an admin override the yearly pool. The allowance is shared
// across the whole household, so editing it from any member row updates the
// same underlying value.
function HouseholdNights({
  member,
  disabled,
  onSaved,
  onError,
  setBusyId,
  instance,
  account,
}) {
  const allowed = Number.isFinite(member.annual_cabin_nights)
    ? member.annual_cabin_nights
    : 30;
  const used = Number.isFinite(member.household_used_nights)
    ? member.household_used_nights
    : 0;
  const [value, setValue] = useState(String(allowed));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(String(allowed));
  }, [allowed]);

  const remaining = Math.max(allowed - used, 0);
  const dirty = String(allowed) !== value.trim();

  async function save() {
    const n = Number(value);
    if (!Number.isInteger(n) || n < 0) {
      onError('Night allowance must be a whole number of 0 or more.');
      return;
    }
    setSaving(true);
    setBusyId(member.id);
    onError(null);
    try {
      const res = await setHouseholdNightAllowanceAdmin(
        instance,
        account,
        member.household_id,
        n,
      );
      if (res.ok) {
        onSaved(res.data && res.data.household);
      } else {
        onError(
          (res.data && (res.data.message || res.data.error)) ||
            'Could not update night allowance.',
        );
      }
    } catch {
      onError('Network error updating night allowance.');
    } finally {
      setSaving(false);
      setBusyId(null);
    }
  }

  return (
    <div className="household-nights">
      <p className="muted member-meta">
        Cabin nights this year: <strong>{used}</strong> used of{' '}
        <strong>{allowed}</strong> ({remaining} left)
      </p>
      <label className="member-control">
        <span>Annual nights</span>
        <span className="household-nights-edit">
          <input
            type="number"
            min="0"
            step="1"
            inputMode="numeric"
            value={value}
            disabled={disabled || saving}
            onChange={(e) => setValue(e.target.value)}
          />
          <button
            type="button"
            className="btn-secondary"
            disabled={disabled || saving || !dirty}
            onClick={save}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </span>
      </label>
    </div>
  );
}
